/* =========================================================================
   LAZ Skincare — featured routine (condensed)

   A short, read-only view of one routine, for a blog post or the homepage.
   Shows each product as a step with a thumbnail, and links through to the
   full routine.

   LSC - 2026-10-19: the step-number badge on each product image was removed
   by request (see buildCard). The order of the cards and the optional step
   names under the images still carry the sequence.

   WHAT IT POINTS AT. A share link. A routine that has never been shared
   exists only in one browser's localStorage, so there is nothing an editorial
   module could reference; a share snapshot is the only stable, public,
   nameable identifier a routine has. The admin builds the routine on
   /pages/routine, presses Share, and pastes the link — into the blog post's
   routine.featured metafield, or into the section's own setting.

   The link also gives the module its destination for free: "see the full
   routine" goes to the same snapshot in shared mode, which already offers
   add-all-to-cart and save-a-copy.

   CONSEQUENCE: this renders in the browser, not in the page HTML. The
   snapshot lives in KV behind the app proxy and Liquid cannot fetch it. The
   module therefore stays hidden until it has something real to show, which is
   deliberate — a marketing block that renders an empty frame when a token has
   expired is worse than one that quietly isn't there.

   PRODUCT DATA comes from /products/{handle}.js rather than the section
   rendering used on the routine page. The condensed card needs a title, an
   image and a URL; the full row markup would be several kilobytes each for
   fields this module throws away. The .js endpoint is small and CDN-cached.
   ========================================================================= */

(function () {
  "use strict";

  const SELECTOR = "[data-lsc-featured-routine]";

  /* Shared across every instance on the page: two modules naming the same
     product cost one request, not two. */
  const productCache = new Map();

  function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);

    const promise = fetch(`/products/${encodeURIComponent(handle)}.js`)
      .then((res) => {
        if (!res.ok) throw new Error(`No product for ${handle}`);
        return res.json();
      })
      /* A product archived since the routine was shared is a gap, not a
         failure: drop it and show the rest. */
      .catch(() => null);

    productCache.set(handle, promise);
    return promise;
  }

  /* Shopify image URLs take a width parameter, so ask for the size actually
     drawn instead of the original, which can be 2000px of unused pixels.
     product.js returns protocol-relative URLs. */
  function sized(url, width) {
    if (!url) return "";
    const absolute = url.indexOf("//") === 0 ? `https:${url}` : url;
    return absolute + (absolute.indexOf("?") === -1 ? "?" : "&") + `width=${width}`;
  }

  /* Accepts a full share URL or a bare token, because an admin pasting from
     the routine page has a URL and an admin who knows the system may paste
     just the token. Anything else resolves to null and the module hides. */
  function tokenFrom(value) {
    const raw = String(value || "").trim();
    if (!raw) return null;

    const inUrl = raw.match(/[?&]token=([A-Za-z0-9_-]{16,64})/);
    if (inUrl) return inUrl[1];

    return /^[A-Za-z0-9_-]{16,64}$/.test(raw) ? raw : null;
  }

  function init(root) {
    if (root.dataset.lscReady === "true") return;
    root.dataset.lscReady = "true";

    const R = window.LazRoutine;
    if (!R) return;

    const token = tokenFrom(root.dataset.routineLink);
    if (!token) return;

    const listEl = root.querySelector("[data-featured-list]");
    const nameEl = root.querySelector("[data-featured-name]");
    const countEl = root.querySelector("[data-featured-count]");
    const moreEl = root.querySelector("[data-featured-more]");
    const ctaEl = root.querySelector("[data-featured-cta]");
    if (!listEl) return;

    const limit = Math.max(1, parseInt(root.dataset.limit, 10) || 6);
    const showStepNames = root.dataset.showStepNames !== "false";
    const routinePath = root.dataset.routineUrl || "/pages/routine";

    /* Step number and display name for a stored step_label, from the Liquid
       table rendered inside this module. */
    let stepMeta = new Map();
    try {
      const raw = root.querySelector("[data-routine-steps]");
      const steps = JSON.parse(raw.textContent);
      stepMeta = new Map(steps.map((s) => [s.label, s]));
    } catch {
      stepMeta = new Map();
    }

    load();

    async function load() {
      let snapshot;
      try {
        const res = await R.readShared(token);
        snapshot = res.routine || res;
      } catch {
        /* Expired, rate limited, or offline. Stay hidden. */
        return;
      }

      const items = (snapshot && snapshot.items) || [];
      if (!items.length) return;

      /* The stored order is the order things were added; the routine's real
         order is by step. Reuse the engine's grouping so this module and the
         routine page can never disagree about sequence. */
      const groups = R.groupByStep(
        items.map((item) => ({ item })),
        { includeEmpty: false }
      );
      const ordered = groups.reduce(
        (all, group) => all.concat(group.entries.map((e) => e.item)),
        []
      );

      const shown = ordered.slice(0, limit);
      const products = await Promise.all(shown.map((item) => fetchProduct(item.handle)));

      const cards = shown
        .map((item, i) => (products[i] ? buildCard(item, products[i]) : null))
        .filter(Boolean);

      /* Every product gone means there is nothing worth showing. */
      if (!cards.length) return;

      cards.forEach((card) => listEl.appendChild(card));

      if (nameEl && snapshot.name) nameEl.textContent = snapshot.name;

      if (countEl) {
        countEl.textContent = `${ordered.length} ${
          ordered.length === 1 ? "product" : "products"
        }`;
      }

      const hidden = ordered.length - cards.length;
      if (moreEl && hidden > 0) {
        moreEl.textContent = `+${hidden} more`;
        moreEl.hidden = false;
      }

      if (ctaEl) {
        ctaEl.href = `${routinePath}?token=${encodeURIComponent(token)}`;
      }

      root.hidden = false;
    }

    function buildCard(item, product) {
      const meta = stepMeta.get(item.step_label);
      const image = sized(product.featured_image, 200);

      const li = document.createElement("li");
      li.className = "lsc-featured-routine__item";

      const link = document.createElement("a");
      link.className = "lsc-featured-routine__card";
      link.href = product.url || `/products/${item.handle}`;

      const media = document.createElement("span");
      media.className = "lsc-featured-routine__media";

      if (image) {
        const img = document.createElement("img");
        img.src = image;
        img.srcset = `${image} 1x, ${sized(product.featured_image, 400)} 2x`;
        img.alt = "";
        img.loading = "lazy";
        img.decoding = "async";
        img.width = 200;
        img.height = 200;
        media.appendChild(img);
      }

      /* LSC - 2026-10-19: the step-number badge that was appended to `media`
         here (a .lsc-featured-routine__step span holding meta.number) has been
         removed by request, so the product image now shows clean. `meta` is
         still read above for the step name below. The
         .lsc-featured-routine__step rule in assets/lsc-routine.css now matches
         nothing and can be deleted the next time that file is edited. */

      link.appendChild(media);

      if (showStepNames && meta && meta.name) {
        const stepName = document.createElement("span");
        stepName.className = "lsc-featured-routine__step-name";
        stepName.textContent = meta.name;
        link.appendChild(stepName);
      }

      const title = document.createElement("span");
      title.className = "lsc-featured-routine__title";
      title.textContent = product.title;
      link.appendChild(title);

      li.appendChild(link);
      return li;
    }
  }

  function boot() {
    document.querySelectorAll(SELECTOR).forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* Re-run when the theme editor swaps a section in. */
  document.addEventListener("shopify:section:load", boot);
})();
